import {
  Button,
  ButtonMenu,
  ButtonModal,
  Checkbox,
  Css,
  type DateRange,
  DateRangeField,
  FieldGroup,
  FormLines,
  MenuItem,
  SelectField,
} from "@homebound/beam";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router";
import {
  createDraftCalendarUrl,
  createDraftGanttUrl,
  createDraftLookaheadUrl,
  createDraftMilestoneUrl,
  createDraftScheduleUrl,
} from "src/RouteUrls";
import { SearchBox } from "src/components";
import { DateOperation, PlanTasksFilter, TaskStatus, TradePartnerTaskStatus } from "src/generated/graphqlTypes";
import { TableActions } from "src/routes/layout/TableActions";
import { isDefined } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { hydrateDateRange, useQueryStorage } from "../../schedule-v2/table/filterUtils";
import { DynamicScheduleView, useScheduleRouteMatch } from "../utils";
import { DynamicSchedulesGroupBy, TaskGroupBy } from "./DynamicSchedulesGroupBy";

export enum TradePartnerStatusFilter {
  All = "All Trade Statuses",
  NotScheduled = "Trade Not Scheduled",
  Scheduled = "Trade Scheduled",
}

type DynamicSchedulesFilterModalWrapperProps<TFilter> = {
  onClose: VoidFunction;
  filter: TFilter;
  setFilter: (filter: TFilter) => void;
  additionalFilters: ReactNode;
  scheduleView: DynamicScheduleView;
};

export function DynamicSchedulesFilterModalWrapper<
  TFilter extends { startDateRange?: DateRange; endDateRange?: DateRange },
>({ onClose, filter, setFilter, additionalFilters, scheduleView }: DynamicSchedulesFilterModalWrapperProps<TFilter>) {
  const [modalFilter, setModalFilter] = useState<TFilter>({ ...mapToState(filter) });

  const onSubmit = useCallback(() => {
    setFilter({ ...(modalFilter satisfies TFilter) });
    onClose();
  }, [modalFilter, setFilter, onClose]);

  return (
    <FormLines>
      <FieldGroup>
        <DateRangeField
          disabled={scheduleView === DynamicScheduleView.Lookahead && "Disabled in lookahead view"}
          value={modalFilter.startDateRange}
          onChange={(val) => setModalFilter((filter) => ({ ...filter, startDateRange: val }))}
          label="Start Date Range"
        />
        <DateRangeField
          value={modalFilter.endDateRange}
          onChange={(val) => setModalFilter((filter) => ({ ...filter, endDateRange: val }))}
          label="Finish Date Range"
        />
      </FieldGroup>
      {additionalFilters}
      <div css={Css.df.gap1.jcfe.$}>
        <Button variant="quaternary" label="Cancel" onClick={onClose} />
        <Button label="Apply" onClick={onSubmit} />
      </div>
    </FormLines>
  );
}

type DynamicSchedulesFilterModalProps = {
  onClose: VoidFunction;
  filter: CustomDynamicSchedulesFilter;
  setFilter: (filter: CustomDynamicSchedulesFilter) => void;
  scheduleView: DynamicScheduleView;
};

export function DynamicSchedulesFilterModal({
  onClose,
  filter,
  setFilter,
  scheduleView,
}: DynamicSchedulesFilterModalProps) {
  const [modalFilter, setModalFilter] = useState<CustomDynamicSchedulesFilter>({ ...mapToState(filter) });

  return (
    <DynamicSchedulesFilterModalWrapper
      scheduleView={scheduleView}
      filter={filter}
      setFilter={(existingFilter) => setFilter({ ...existingFilter, ...modalFilter })}
      onClose={onClose}
      additionalFilters={
        <>
          <div css={Css.df.gap2.$}>
            <Checkbox
              label="Show Completed"
              selected={!!modalFilter.includeCompletedTasks}
              onChange={(val) => setModalFilter((filter) => ({ ...filter, includeCompletedTasks: val }))}
            />
            <Checkbox
              label="Show Milestones"
              selected={!!modalFilter.includeMilestones}
              onChange={(val) => setModalFilter((filter) => ({ ...filter, includeMilestones: val }))}
            />
          </div>
          <SelectField
            value={modalFilter.tradePartnerStatus}
            onSelect={(val) => setModalFilter((filter) => ({ ...filter, tradePartnerStatus: val! }))}
            label="Trade Partner Status"
            options={[
              { value: TradePartnerStatusFilter.All, label: "All Trade Statuses" },
              { value: TradePartnerStatusFilter.NotScheduled, label: "Trade Not Scheduled" },
              { value: TradePartnerStatusFilter.Scheduled, label: "Trade Scheduled" },
            ]}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
          />
        </>
      }
    />
  );
}

export type CustomDynamicSchedulesFilter = Pick<PlanTasksFilter, "status"> & {
  startDateRange: DateRange | undefined;
  endDateRange: DateRange | undefined;
  includeCompletedTasks: boolean;
  includeMilestones: boolean;
  groupBy: TaskGroupBy;
  tradePartnerStatus: TradePartnerStatusFilter | undefined;
};

export const customDynamicSchedulesFilterDefault: CustomDynamicSchedulesFilter = {
  startDateRange: undefined,
  endDateRange: undefined,
  includeCompletedTasks: false,
  includeMilestones: false,
  groupBy: TaskGroupBy.None,
  tradePartnerStatus: TradePartnerStatusFilter.All,
};

export function mapToFilter(filter: CustomDynamicSchedulesFilter): PlanTasksFilter {
  const { startDateRange, endDateRange, includeCompletedTasks, status, groupBy, ...otherFilters } = filter;
  const hydratedStartDateRange = hydrateDateRange(startDateRange);
  const hydratedEndDateRange = hydrateDateRange(endDateRange);
  // There is no status filter in the UI
  const nonComplete = Object.values(TaskStatus).filter((status) => status !== TaskStatus.Complete);
  return {
    ...otherFilters,
    ...(hydratedStartDateRange?.from && hydratedStartDateRange.to
      ? {
          startDateRange: {
            op: DateOperation.Between,
            value: hydratedStartDateRange.from,
            value2: hydratedStartDateRange.to,
          },
        }
      : {}),
    ...(hydratedEndDateRange?.from && hydratedEndDateRange.to
      ? {
          endDateRange: {
            op: DateOperation.Between,
            value: hydratedEndDateRange.from,
            value2: hydratedEndDateRange.to,
          },
        }
      : {}),
    status: [...(status ?? nonComplete), ...(includeCompletedTasks ? [TaskStatus.Complete] : [])],
    tradePartnerStatus: TradeStatusFilterToStatuses[filter.tradePartnerStatus ?? TradePartnerStatusFilter.All],
  };
}

export function mapToState<TFilter extends { startDateRange?: DateRange; endDateRange?: DateRange }>(
  filter: TFilter,
): TFilter {
  const { startDateRange, endDateRange } = filter;
  const hydratedStartDateRange = hydrateDateRange(startDateRange);
  const hydratedEndDateRange = hydrateDateRange(endDateRange);
  return { ...filter, startDateRange: hydratedStartDateRange, endDateRange: hydratedEndDateRange };
}

const filterStorageKey = "dynamicSchedulesFilter";

export function FilterSubHeader({
  scheduleParentId,
  rightMenuEl,
  leftMenuEl,
}: {
  scheduleParentId: string;
  rightMenuEl?: ReactNode;
  leftMenuEl?: ReactNode;
}) {
  const scheduleView = useScheduleRouteMatch();
  const history = useHistory();
  const [, setSearch] = useState("");

  const { queryStorage, setQueryStorage } = useQueryStorage({
    storageKey: filterStorageKey,
    initialQueryStorage: customDynamicSchedulesFilterDefault,
  });

  const showFilterModal = useCallback(
    (close: VoidFunction) => {
      return (
        <DynamicSchedulesFilterModal
          filter={queryStorage}
          setFilter={setQueryStorage}
          onClose={close}
          scheduleView={scheduleView}
        />
      );
    },
    [queryStorage, setQueryStorage, scheduleView],
  );

  const handleClear = useCallback(() => {
    setQueryStorage({} as CustomDynamicSchedulesFilter);
  }, [setQueryStorage]);

  const hasFilter = useMemo(() => Object.values(queryStorage).some(Boolean), [queryStorage]);

  const draftViewMenuItems: MenuItem[] = [
    { label: "List", onClick: createDraftScheduleUrl(scheduleParentId) },
    { label: "Calendar", onClick: createDraftCalendarUrl(scheduleParentId) },
    { label: "Gantt", onClick: createDraftGanttUrl(scheduleParentId) },
    {
      label: "Milestone",
      onClick: () => {
        // remove query param filters for milestone view
        history.push(createDraftMilestoneUrl(scheduleParentId));
        setQueryStorage({} as CustomDynamicSchedulesFilter);
      },
    },
    {
      label: "Lookahead",
      onClick: () => {
        // remove query param filters for lookahead view
        history.push(createDraftLookaheadUrl(scheduleParentId));
        setQueryStorage({} as CustomDynamicSchedulesFilter);
      },
    },
  ];

  return (
    <TableActions>
      <div css={Css.df.gap1.$}>
        {/* hiding filter modal from milestone view */}
        {scheduleView !== DynamicScheduleView.Milestone && (
          <ButtonModal content={showFilterModal} trigger={{ label: "Filter" }} />
        )}
        {[DynamicScheduleView.List, DynamicScheduleView.Gantt].includes(scheduleView) && (
          <DynamicSchedulesGroupBy queryStorage={queryStorage} setQueryStorage={setQueryStorage} />
        )}
        <SearchBox clearable onSearch={setSearch} />
        {hasFilter && <Button variant="text" label="Clear" onClick={handleClear} />}
      </div>
      <div css={Css.df.gap1.$}>
        {leftMenuEl && leftMenuEl}
        <ButtonMenu items={draftViewMenuItems} trigger={{ label: `View: ${scheduleView}` }} />
        {rightMenuEl && rightMenuEl}
      </div>
    </TableActions>
  );
}

export function useDynamicSchedulesFilter() {
  const { queryStorage: allFilters } = useQueryStorage({
    storageKey: filterStorageKey,
    initialQueryStorage: customDynamicSchedulesFilterDefault,
  });
  const [{ search }] = useQueryParams({ search: StringParam });
  const maybeSearchFilter = isDefined(search) && search !== "" ? { search } : {};

  return {
    allFilters,
    search,
    // queryFilter can be passed directly to any query input expecting `PlanTasksFilter`
    queryFilter: { ...mapToFilter(allFilters), ...maybeSearchFilter },
  };
}

const TradeStatusFilterToStatuses = {
  [TradePartnerStatusFilter.All]: undefined,
  [TradePartnerStatusFilter.NotScheduled]: [
    TradePartnerTaskStatus.NotSent,
    TradePartnerTaskStatus.NeedsConfirmation,
    TradePartnerTaskStatus.NeedsReconfirmation,
    TradePartnerTaskStatus.Unavailable,
  ],
  [TradePartnerStatusFilter.Scheduled]: [TradePartnerTaskStatus.CompletedJob, TradePartnerTaskStatus.Confirmed],
};
